import { useMemo } from 'react';
import {
  Accordion,
  AccordionItem,
  Form,
  Select,
  SelectItem,
  SkeletonPlaceholder,
  TextInput,
  Tile,
  Toggle
} from '@carbon/react';
import cx from 'clsx';
import { noop } from 'lodash';

import { useNotifications } from 'core/notifications';
import { useIsReadonly } from 'core/readonly';
import ProjectFormActions from 'features/inputs/components/ProjectFormActions';
import { useTrackProjectFormTabDirtyStatus } from 'features/inputs/components/ProjectFormNavigation';
import { useMeasurementSystem } from 'features/inputs/contexts/measurementSystemContext';
import useBusinessUnitsQuery from 'shared/api/business-units/useBusinessUnitsQuery';
import useMutateUpdateProjectAdditionalSpaces from 'shared/api/projects/useMutateUpdateProjectAdditionalSpaces';
import useSpecialtySpacesQuery from 'shared/api/specialty-spaces/useSpecialtySpacesQuery';
import SiteSpecificSpaces from 'shared/site-specific-spaces/SiteSpecificSpaces';
import useSiteSpecificSpacesController from 'shared/site-specific-spaces/useSiteSpecificSpacesController';
import useSpecialtySpacesController from 'shared/specialty-spaces/useSpecialtySpacesController';
import useUpdateEffect from 'utils/hooks/useUpdateEffect';
import styles from 'theme/features/inputs/additional-spaces-form.module.scss';

const SpecialtySpaceAccordionItem = ({
  id,
  name,
  defaultBusinessUnitId,
  isIncluded,
  onToggle,
  register,
  isReadonly
}) => {
  const { data: businessUnits } = useBusinessUnitsQuery();

  const defaultBusinessUnit = useMemo(
    () => businessUnits.find((b) => b.id === defaultBusinessUnitId),
    [defaultBusinessUnitId, businessUnits]
  );

  return (
    <AccordionItem
      key={id}
      open={isIncluded}
      className={cx(isReadonly && styles['accordion--disabled'])}
      onHeadingClick={isReadonly ? undefined : onToggle}
      renderToggle={({ children }) => (
        <div className={styles['accordion__title']} onClick={isReadonly ? undefined : onToggle}>
          {children}
        </div>
      )}
      title={
        <div className={styles['accordion__title-action']}>
          <Toggle
            id={`specialty-space-${id}`}
            labelText={name}
            labelA="Exclude"
            labelB="Include"
            toggled={isIncluded}
            className={styles.toggle}
          />
          <strong>{name}</strong>
        </div>
      }
    >
      <div className={styles['accordion__content']}>
        <TextInput
          id={`specialty-space-${id}-area`}
          className={styles['specialty-space-input']}
          labelText="Total Estimated Area (if known)"
          placeholder={isReadonly ? '-' : 'Enter a number'}
          min={0}
          type="number"
          {...register(`${id}.area`)}
          readOnly={isReadonly}
        />
        {defaultBusinessUnit ? (
          <p className={styles['default-business-unit']}>Owned by {defaultBusinessUnit.name}</p>
        ) : (
          <Select
            id="business-unit"
            className={styles['specialty-space-input']}
            labelText="Business Unit"
            {...register(`${id}.businessUnit`)}
          >
            <SelectItem text="" value="" />
            {businessUnits?.map((x) => (
              <SelectItem key={x.id} value={x.id} text={x.name} />
            ))}
          </Select>
        )}
      </div>
    </AccordionItem>
  );
};

const AdditionalSpacesForm = ({
  specialtySpaces: specialtySpacesValues,
  siteSpecificSpaces,
  onSaveChanges,
  projectId
}) => {
  const { showErrorNotification, showSuccessNotification } = useNotifications();

  const isReadonly = useIsReadonly();
  const measurementSystem = useMeasurementSystem();

  const { data: specialtySpaces } = useSpecialtySpacesQuery();
  const { data: businessUnits } = useBusinessUnitsQuery();

  const updateProjectAdditionalSpacesMutation = useMutateUpdateProjectAdditionalSpaces({
    onError: () => showErrorNotification({ description: 'Failed to update specialty & site-specific spaces' }),
    onSuccess: (project) => {
      onSaveChanges(project);
      showSuccessNotification({ description: 'Project specialty & site-specific spaces updated successfully' });
    }
  });

  const siteSpecificSpacesForm = useSiteSpecificSpacesController({
    values: siteSpecificSpaces,
    onSubmit: noop
  });

  const specialtySpacesForm = useSpecialtySpacesController({
    values: specialtySpacesValues,
    onSubmit: (values) =>
      updateProjectAdditionalSpacesMutation.mutate({
        id: projectId,
        specialtySpaces: values,
        siteSpecificSpaces: siteSpecificSpacesForm.getValues()
      })
  });

  useTrackProjectFormTabDirtyStatus([specialtySpacesForm.control, siteSpecificSpacesForm.control]);

  useUpdateEffect(() => siteSpecificSpacesForm.reset(siteSpecificSpaces), [siteSpecificSpaces]);

  return (
    <div>
      <Tile className={styles.heading}>
        <h4>
          <strong>Specialty Spaces</strong>
        </h4>
        <div>Predefined special space types that are applicable to a site</div>
      </Tile>
      <Form onSubmit={specialtySpacesForm.onSubmit}>
        <Accordion className={styles.accordion}>
          {specialtySpaces && businessUnits ? (
            specialtySpaces.map((x) => {
              const isIncluded = specialtySpacesForm.values[x.id]?.enabled ?? false;
              const businessUnit = specialtySpacesForm.values[x.id]?.businessUnit ?? '';

              return (
                <SpecialtySpaceAccordionItem
                  key={x.id}
                  {...x}
                  register={specialtySpacesForm.register}
                  isIncluded={isIncluded}
                  isReadonly={isReadonly}
                  onToggle={() => {
                    specialtySpacesForm.setValue(
                      x.id.toString(),
                      { enabled: !isIncluded, area: '', businessUnit },
                      { shouldDirty: true }
                    );
                  }}
                />
              );
            })
          ) : (
            <SkeletonPlaceholder className={styles.skeleton} />
          )}
        </Accordion>
      </Form>
      <SiteSpecificSpaces
        withHeader
        spaces={siteSpecificSpacesForm.spaces}
        onAddSpace={siteSpecificSpacesForm.add}
        onEditSpace={siteSpecificSpacesForm.edit}
        onRemoveSpace={siteSpecificSpacesForm.remove}
        measurementSystem={measurementSystem}
      />
      <ProjectFormActions projectId={projectId} onPrimaryClick={specialtySpacesForm.onSubmit} />
    </div>
  );
};

export default AdditionalSpacesForm;
